import { useTranslation } from "react-i18next";
import type { Currency, Locale, Product } from "@nh/shared";
import { ProductCard } from "./ProductCard";

export interface ProductGridProps {
  products: readonly Product[];
  currency: Currency;
  locale: Locale;
}

/**
 * La rejilla del catálogo: una columna en teléfono, dos en tableta, tres
 * en escritorio. Las mismas tres paradas del sistema actual.
 *
 * ── Cada tarjeta enlaza a su ficha ───────────────────────────────────
 * `/producto/:slug`, y el slug lo compone esta rejilla y no la tarjeta:
 * la tarjeta ya sabe pintarse con enlace o sin él, y quién es su destino
 * es una decisión de la pantalla que la coloca. La ficha se lleva bien
 * con `slug` y no con `id` porque el slug es lo que se comparte y lo que
 * indexa un buscador — y no cambia nunca, por contrato.
 */
export function ProductGrid({ products, currency, locale }: ProductGridProps) {
  const { t } = useTranslation();

  return (
    // El nombre de la lista la distingue de las otras de la página (las
    // del pie): sin él, "lista" a secas no le dice nada a quien navega
    // saltando entre regiones.
    <ul
      aria-label={t("Productos")}
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
    >
      {products.map((product) => (
        <li key={product.id}>
          <ProductCard
            product={product}
            currency={currency}
            locale={locale}
            to={`/producto/${product.slug}`}
          />
        </li>
      ))}
    </ul>
  );
}
